import { useAuth } from "@contexts/auth";
import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineFileImage, AiOutlineFilePdf } from "react-icons/ai";
import { LiaFileUploadSolid } from "react-icons/lia";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconInfoCircle, IconTrash } from "@tabler/icons-react";

import { ConsultationSelect } from "@components/ConsultationSelect";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { documentFileTypes, imageFileTypes } from "@constants/fileTypes";
import {
  MAX_FILENAME_LENGTH_BEFORE_UPLOAD,
  MAX_PAGE_SIZE,
  MEGABYTE,
} from "@constants/index";

import { useCreatePrescription } from "@hooks/mutations/prescription/useCreatePrescription";
import { useGetConsultationsForPatient } from "@hooks/queries/consultation/useGetConsultationsForPatient";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";

import { Prescription } from "@customTypes/prescription";

import { StyledEditModalContent } from "../EditModal/EditModal.styled";


interface AddDialogProps {
  opened: boolean;
  close: () => void;
}

export const AddModal = ({ opened, close }: AddDialogProps) => {
  const { t, i18n } = useTranslation();

  const { patientId } = useParams();
  const { id: actorId, fullName } = useAuth();

  const [uploadedFiles, setUploadedFiles] = useState<File[] | []>([]);
  const [consultationId, setConsultationId] = useState<string | null>(null);
  const [prescriptionDate, setPrescriptionDate] = useState<Date | null>(
    new Date()
  );
  const [formData, setFormData] = useState({} as Prescription);
  const [doctor, setDoctor] = useState<ComboboxItem | null>({ value: actorId, label: fullName });

  const {
    data: consultationsData,
    isSuccess: isConsultationsSuccess,
    isLoading: isConsultationsLoading,
    isRefetching: isConsultationsRefetching,
  } = useGetConsultationsForPatient({
    patientId: patientId as string,
    hasPrescription: false,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const { mutate: createPrescription, isLoading: isCreatePrescriptionLoading } =
    useCreatePrescription();
  const {
    data: doctorsData,
    isLoading: isDoctorsLoading,
    isRefetching: isDoctorsRefetching,
  } = useGetUsers({
    permission: "prescriptions.create",
  });

  const consultationsComboboxItems = useMemo(() => {
    return consultationsData?.newData?.map(item => ({
      label: item.cId,
      value: item.id,
      date: `${formatDateToString(item.date)}, ${item.date.toLocaleTimeString(
        i18n.language,
        { hour: "2-digit", minute: "2-digit" }
      )}`,
    }));
  }, [consultationsData]);
  const doctorsComboboxItems = useMemo(() => {
    return doctorsData?.filter(item => item.id === actorId).map(item => ({
      label: item.fullName,
      value: item.id,
    }));
  }, [doctorsData, actorId]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": imageFileTypes,
      "application/pdf": documentFileTypes,
    },
    onDrop,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleCreatePrescription = () => {
    if (!doctor) return;

    const jsFormData = new FormData();

    uploadedFiles?.length &&
      uploadedFiles.forEach(file => {
        jsFormData.append(`files`, file);
      });
    jsFormData.append(
      "prescription_date",
      prescriptionDate
        ? prescriptionDate.toISOString()
        : new Date().toISOString()
    );

    jsFormData.append("consultation_id", formData?.consultationId ?? "");
    jsFormData.append("patient_id", patientId as string);
    jsFormData.append("prescribed_by", doctor?.value ?? "");
    jsFormData.append(
      "prescription_date",
      prescriptionDate
        ? prescriptionDate.toISOString()
        : new Date().toISOString()
    );
    jsFormData.append("fast_consultation", String(false));

    console.group("jsFormData");
    jsFormData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();
    createPrescription(jsFormData, {
      onSuccess: () => {
        setFormData({} as Prescription);
        setConsultationId(null);
        setUploadedFiles([]);
        setPrescriptionDate(new Date());
        close();
      },
    });
  };

  useEffect(() => {
    if (consultationId) {
      setFormData(prevData => ({
        ...prevData,
        consultationId,
      }));
    }
  }, [consultationId]);

  useEffect(() => {
    if (formData) console.log("[Create Prescription formData]", formData);
  }, [formData]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title={<Text fw={500}>{t("add-prescription")}</Text>}
      styles={{ body: { minHeight: "auto" } }}>
      <StyledEditModalContent>
        <Tabs defaultValue="info">
          <Tabs.List grow>
            <Tabs.Tab value="info">{t("info")}</Tabs.Tab>
            <Tabs.Tab value="add-files">{t("add-files")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info">
            <form className="form">
              {isConsultationsSuccess && !consultationsData?.newData?.length ? (
                <Alert
                  // w={"fit-content"}
                  variant="light"
                  color="blue"
                  icon={<IconInfoCircle />}>
                  <Text fz={14} lh={1.5} c="blue">
                    {t("no-consultation-for-prescription")}
                  </Text>
                </Alert>
              ) : null}
              <div className="form-field">
                <ConsultationSelect
                  label={t("consultation")}
                  placeholder={t("select-consultation")}
                  isLoading={
                    isConsultationsLoading || isConsultationsRefetching
                  }
                  disabled={
                    !consultationsData?.newData?.length &&
                    isConsultationsSuccess
                  }
                  data={
                    !consultationsComboboxItems?.length
                      ? []
                      : consultationsComboboxItems
                  }
                  value={formData.consultationId}
                  setValue={setConsultationId}
                />
              </div>

              <div className="form-field">
                <DatePickerInput
                  label={t("prescription-date")}
                  value={prescriptionDate}
                  onChange={setPrescriptionDate}
                  leftSection={<IconCalendar size={16} stroke={1.5} />}
                />
              </div>
              <div className="form-field">
                <Select
                  label={t("doctor")}
                  placeholder={t("select-doctor")}
                  withAsterisk
                  data={doctorsComboboxItems || []}
                  value={doctor?.value ?? null}
                  onChange={(_val, option) => setDoctor(option)}
                  searchable
                  rightSection={
                    isDoctorsLoading || isDoctorsRefetching ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                />
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="add-files">
            <div className="files-wrapper">
              <div
                {...getRootProps({
                  className: `dropzone ${isDragActive ? "active" : ""} ${
                    isDragAccept ? "accept" : ""
                  } ${isDragReject ? "reject" : ""}`,
                })}>
                <input {...getInputProps()} />

                <div className="text-content">
                  <div className="svg-wrapper">
                    <LiaFileUploadSolid />
                  </div>
                  <div className="p-wrapper">
                    <p>{t("drag-file-here")}</p>
                    <p>{t("drag-file-here-continuation")}</p>
                  </div>
                </div>
              </div>
              <div className="uploaded-files">
                <h4>{t("uploaded-files")}</h4>
                {!uploadedFiles.length ? (
                  <Alert
                    mt={"md"}
                    variant="light"
                    color="blue"
                    icon={<IconInfoCircle />}>
                    <Text fz={14} lh={1.5} c="blue">
                      {t("files-appear-info")}
                    </Text>
                  </Alert>
                ) : (
                  <ScrollAreaAutosize type="auto" h={200} mt="sm">
                    <ul>
                      {uploadedFiles.map(file => (
                        <li key={file.name}>
                          <div className="content">
                            {file.type === "application/pdf" ? (
                              <AiOutlineFilePdf />
                            ) : (
                              <AiOutlineFileImage />
                            )}
                            <p>
                              {truncateWithEllipsis(
                                file.name,
                                MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                              )}{" "}
                              ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                            </p>
                          </div>

                          <ActionIcon
                            color="red"
                            variant="outline"
                            onClick={() => removeFile(file)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </li>
                      ))}
                    </ul>
                  </ScrollAreaAutosize>
                )}
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>

        <Group justify="end" mt="xl" pb={uploadedFiles.length ? "md" : "0"}>
          <PermissionGate permission="prescriptions.create"><Button
            onClick={handleCreatePrescription}
            disabled={!formData?.consultationId || !prescriptionDate || !doctor}
            loading={isCreatePrescriptionLoading}>
            {t("add-prescription")}
          </Button></PermissionGate>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
